/**
 * Pure helpers of the messaging functions (no Deno or network code, so the
 * core tests can check them): phone numbers for SMS and the branded e-mail.
 */
import { legalIdentityOf, operatorLine, type LegalIdentity } from "./address.ts";

/**
 * International form without "+" (what SMS providers want), or null if it does
 * not look like a phone number. Hungarian habits are understood: 06 30 123 4567,
 * +36 30 123 4567, 0036..., 30/123-4567 (a national number of the default country).
 */
export function normalizePhone(raw: string, defaultCountryCode = "36"): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  let digits = trimmed.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) digits = digits.slice(1);
  else if (digits.startsWith("00")) digits = digits.slice(2);
  else if (defaultCountryCode === "36" && digits.startsWith("06")) digits = "36" + digits.slice(2);
  else if (digits.startsWith("0")) digits = defaultCountryCode + digits.slice(1);
  else if (!digits.startsWith(defaultCountryCode)) digits = defaultCountryCode + digits;
  if (/\D/.test(digits)) return null;
  return digits.length >= 10 && digits.length <= 15 ? digits : null;
}

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Escaped text with line breaks and clickable https links. */
function textToHtml(text: string): string {
  return escapeHtml(text)
    .replace(/https:\/\/[^\s<]+/g, (u) => `<a href="${u}" style="color: #0A7D68; word-break: break-all;">${u}</a>`)
    .replace(/\r?\n/g, "<br>");
}

/** Logo images in e-mails: only a https link (the signed storage URL), never anything else. */
export function safeLogoUrl(url: string | null | undefined): string | null {
  return typeof url === "string" && /^https:\/\/[^\s"'<>]+$/.test(url) ? url : null;
}

/**
 * The header of every customer e-mail. Without a logo: the workshop's name on the dark band (as before).
 * With the workshop's own logo (owner, 2026-11-03): a white band, the logo on the left (at most 56 px high,
 * any logo shows on white) and the workshop's name beside it; the alt text is the name, so a mail program
 * that blocks images still shows who wrote.
 */
export function emailHeaderHtml(workshopName: string, logoUrl?: string | null): string {
  const name = escapeHtml(workshopName);
  const url = safeLogoUrl(logoUrl);
  if (!url) return `<div style="background: #0E2433; padding: 16px 24px; font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-size: 24px; font-weight: bold; letter-spacing: 0.5px; color: #ffffff;">${name}</div>`;
  return `<div style="background: #ffffff; padding: 16px 24px; border-bottom: 3px solid #0E2433;"><table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse: collapse;"><tr>`
    + `<td style="padding: 0 14px 0 0; vertical-align: middle;"><img src="${escapeHtml(url)}" alt="${name}" height="56" style="display: block; height: 56px; width: auto; max-width: 200px; border: 0;"></td>`
    + `<td style="vertical-align: middle; font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-size: 24px; font-weight: bold; letter-spacing: 0.5px; color: #0E2433;">${name}</td>`
    + `</tr></table></div>`;
}

// ---- the workshop's contacts in every customer message (owner, 2026-11-04: "the customer should never have to copy anything") ----

/** What a customer can use to reach the workshop; a missing item is null and its line is left out. */
export interface WorkshopContacts {
  phone: string | null;
  email: string | null;
  /** The workshop address where the car is (not the registered seat). */
  address: string | null;
  /** Who runs the workshop (legal name, seat, tax and registration number): the small line at the bottom. */
  legal?: LegalIdentity | null;
}

const settingText = (g: Record<string, unknown>, k: string) => {
  const v = g[k];
  return typeof v === "string" && v.trim() ? v.trim() : null;
};

const EMAIL_RE = /^[^\s@<>"'(),;:]+@[^\s@<>"'(),;:]+\.[^\s@<>"'(),;:]+$/;

/**
 * The contacts from the workshop's settings (settings.general). The address follows siteAddressOf in
 * packages/core/src/readyNotice.ts and public.workshop_site_address (a test checks it): the seat when the tick
 * "the workshop is at the seat" is on, otherwise the workshop address (the seat if that is empty).
 */
export function workshopContactsOf(general: unknown): WorkshopContacts {
  const g = (general && typeof general === "object" ? general : {}) as Record<string, unknown>;
  const seat = settingText(g, "seatAddress") ?? settingText(g, "workshopAddress");
  const same = !(g.workshopAddressSameAsSeat === false || g.workshopAddressSameAsSeat === "false");
  const email = settingText(g, "workshopEmail");
  return {
    phone: settingText(g, "workshopPhone"),
    email: email && EMAIL_RE.test(email) ? email : null,
    address: same ? seat : settingText(g, "workshopAddress") ?? seat,
    legal: legalIdentityOf(g),
  };
}

/**
 * The number in the international form phones turn into a link by themselves: "+36 30 123 4567",
 * "+36 1 234 5678"; other countries "+43664..." without grouping. Null when it is not a phone number.
 */
export function displayPhone(raw: string | null | undefined, defaultCountryCode = "36"): string | null {
  const n = raw ? normalizePhone(raw, defaultCountryCode) : null;
  if (!n) return null;
  if (!n.startsWith("36") || (n.length !== 10 && n.length !== 11)) return `+${n}`;
  const rest = n.slice(2);
  const area = rest.startsWith("1") ? "1" : rest.slice(0, 2);
  const sub = rest.slice(area.length);
  const grouped = sub.length === 7 ? `${sub.slice(0, 3)} ${sub.slice(3)}` : sub.length === 6 ? `${sub.slice(0, 3)} ${sub.slice(3)}` : sub;
  return `+36 ${area} ${grouped}`;
}

/** A mobile number (an SMS can go to it). Known for Hungary (20, 30, 31, 50, 70); elsewhere unknown, so no. */
export function isMobilePhone(raw: string | null | undefined): boolean {
  const n = raw ? normalizePhone(raw) : null;
  return !!n && /^36(20|30|31|50|70)\d{7}$/.test(n);
}

/** Route links to an address (keep equal to googleMapsRoute / wazeRoute in packages/core/src/readyNotice.ts; a test checks it). */
export const mapsRouteLink = (address: string) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address.trim())}`;
export const wazeRouteLink = (address: string) => `https://waze.com/ul?q=${encodeURIComponent(address.trim())}&navigate=yes`;

/** The clickable form of the contacts: what the e-mails and the customer's pages show. */
export interface ContactLinks {
  phone: { display: string; tel: string; sms: string | null } | null;
  email: { address: string; mailto: string } | null;
  address: { text: string; google: string; waze: string } | null;
}

export function contactLinks(c: WorkshopContacts | null | undefined): ContactLinks {
  const n = c?.phone ? normalizePhone(c.phone) : null;
  const email = c?.email?.trim() && EMAIL_RE.test(c.email.trim()) ? c.email.trim() : null;
  const address = c?.address?.trim() || null;
  return {
    phone: n ? { display: displayPhone(c!.phone)!, tel: `tel:+${n}`, sms: isMobilePhone(c!.phone) ? `sms:+${n}` : null } : null,
    email: email ? { address: email, mailto: `mailto:${email}` } : null,
    address: address ? { text: address, google: mapsRouteLink(address), waze: wazeRouteLink(address) } : null,
  };
}

/** Whether the contacts have anything to show. */
export const hasContacts = (l: ContactLinks) => !!(l.phone || l.email || l.address);

/** The labels of a language ("contacts.*" keys, CONTACT_TEXTS of contactTexts.ts); Hungarian when unknown. */
export function contactLabels(all: Record<string, Record<string, string>>, language: string): Record<string, string> {
  return all[language] ?? all.hu ?? {};
}

/** The plain text part of an e-mail: the same contacts as lines (a line without data is left out). */
export function contactsText(c: WorkshopContacts | null | undefined, labels: Record<string, string>): string {
  const l = contactLinks(c);
  if (!hasContacts(l)) return "";
  return [
    labels.title,
    l.phone ? `${labels.phone}: ${l.phone.display}` : "",
    l.email ? `${labels.email}: ${l.email.address}` : "",
    l.address ? `${labels.address}: ${l.address.text}` : "",
    l.address ? `${labels.google}: ${l.address.google}` : "",
    l.address ? `${labels.waze}: ${l.address.waze}` : "",
  ].filter(Boolean).join("\n");
}

const contactButton = (href: string, label: string) =>
  `<a href="${escapeHtml(href)}" style="display: inline-block; box-sizing: border-box; max-width: 100%; margin: 6px 6px 0 0; padding: 8px 14px; border: 2px solid #0A7D68; border-radius: 0; color: #0A7D68; background: #ffffff; font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-weight: bold; font-size: 16px; letter-spacing: 0.5px; text-transform: uppercase; text-decoration: none;">${escapeHtml(label)}</a>`;
// caption above the value (one column), so the box fits a phone's mail app too
const contactRow = (caption: string, body: string) =>
  `<tr><td style="padding: 10px 0; vertical-align: top;"><div style="font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-size: 14px; font-weight: bold; letter-spacing: 0.5px; text-transform: uppercase; color: #3D4E5B;">${escapeHtml(caption)}</div>${body}</td></tr>`;

/**
 * The contacts box of every customer e-mail (Munkalap style: ink frame, dashed row lines, square buttons):
 * phone with Call (and SMS for a mobile), e-mail as a mailto link, the workshop address with Google Maps and Waze.
 * Empty when the workshop gave none.
 */
export function contactsEmailHtml(c: WorkshopContacts | null | undefined, labels: Record<string, string>): string {
  const l = contactLinks(c);
  if (!hasContacts(l)) return "";
  const link = (href: string, text: string, mono = false) =>
    `<a href="${escapeHtml(href)}" style="color: #0A7D68; font-weight: bold; text-decoration: underline;${mono ? " font-family: 'IBM Plex Mono', Consolas, monospace;" : " word-break: break-all;"}">${escapeHtml(text)}</a>`;
  const rows = [
    l.phone ? contactRow(labels.phone ?? "", `${link(l.phone.tel, l.phone.display, true)}<br>${contactButton(l.phone.tel, labels.call ?? "")}${l.phone.sms ? contactButton(l.phone.sms, labels.sms ?? "") : ""}`) : "",
    l.email ? contactRow(labels.email ?? "", link(l.email.mailto, l.email.address)) : "",
    l.address ? contactRow(labels.address ?? "", `${escapeHtml(l.address.text)}<br>${contactButton(l.address.google, labels.google ?? "")}${contactButton(l.address.waze, labels.waze ?? "")}`) : "",
  ].filter(Boolean);
  return `<div style="margin: 0 24px 24px; border: 2px solid #0E2433; border-radius: 0; padding: 12px 16px;">`
    + `<div style="font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-size: 18px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; color: #0E2433; padding-bottom: 6px; border-bottom: 1px dashed #DCE3E8;">${escapeHtml(labels.title ?? "")}</div>`
    + `<table role="presentation" cellpadding="0" cellspacing="0" style="border-collapse: collapse; width: 100%; font-size: 15px;">${rows.join(`<tr><td style="border-top: 1px dashed #DCE3E8; font-size: 0; line-height: 0; height: 1px;">&nbsp;</td></tr>`)}</table>`
    + `</div>`;
}

/**
 * An SMS always carries the workshop's number in the form phones auto link ("Tel.: +36 30 123 4567"), unless the
 * text already has that number (in any form). The text itself stays short: the link to the customer page is in it.
 */
export function smsWithPhone(text: string, phone: string | null | undefined, line: string): string {
  const shown = displayPhone(phone);
  const n = phone ? normalizePhone(phone) : null;
  if (!shown || !n) return text;
  const present = (text.match(/\+?\d[\d ()/.-]{6,}\d/g) ?? []).some((m) => normalizePhone(m) === n);
  return present ? text : `${text.trimEnd()}\n${line.replace("{{phone}}", shown)}`;
}

/** The contacts of a message: the workshop's data and the labels in its language, both optional. */
export interface MessageContacts {
  info: WorkshopContacts | null;
  labels: Record<string, string>;
  /** The workshop's (trade) name: the legal line leaves out an operator of the same name. */
  workshopName?: string | null;
}

/**
 * The small legal line of a customer e-mail ("Üzemeltető: Vitamin Kft. · 1151 Budapest, ... · Adószám: ..."), from
 * the contacts' legal identity and the "contacts.*" labels; empty when there is nothing to show.
 */
export function operatorLineOf(contacts: MessageContacts | null | undefined, workshopName?: string | null): string {
  if (!contacts?.info?.legal) return "";
  const l = contacts.labels;
  return operatorLine(contacts.info.legal, workshopName ?? contacts.workshopName ?? null, {
    operator: l.operator ?? "", taxNumber: l.taxNumber ?? "", regNumber: l.regNumber ?? "", soleTraderNumber: l.soleTraderNumber ?? "",
  }) ?? "";
}

/** The e-mail footer: the legal line (when there is one) above the small Garagevo mark. */
function footerHtml(contacts: MessageContacts | null | undefined, workshopName: string): string {
  const legal = operatorLineOf(contacts, workshopName);
  return `<div style="border-top: 1px solid #DCE3E8; padding: 14px 24px; font-size: 12px; color: #3D4E5B;">`
    + (legal ? `<div style="margin: 0 0 6px;">${escapeHtml(legal)}</div>` : "")
    + `<strong>garag<span style="color: #0A7D68;">evo</span></strong></div>`;
}

/**
 * The quote e-mail in the Garagevo brand: the workshop's name first (dark band),
 * the text written by the program in the workshop's language, a button to the link,
 * then the workshop's clickable contacts.
 */
export function quoteEmailHtml(p: { workshopName: string; text: string; link: string; buttonLabel: string; logoUrl?: string | null; contacts?: MessageContacts | null }): string {
  return `<!doctype html><html><body style="margin: 0; background: #F2F5F7;">
<div style="background: #F2F5F7; padding: 24px 12px; font-family: 'Atkinson Hyperlegible Next', Verdana, Arial, Helvetica, sans-serif; font-size: 16px; line-height: 1.55; color: #0E2433;">
<div style="max-width: 560px; margin: 0 auto; background: #ffffff; border: 2px solid #0E2433; border-radius: 0; overflow: hidden;">
${emailHeaderHtml(p.workshopName, p.logoUrl)}
<div style="padding: 24px;">
<p style="margin: 0 0 18px;">${textToHtml(p.text)}</p>
<p style="margin: 22px 0;"><a href="${escapeHtml(p.link)}" style="display: inline-block; background: #0A7D68; color: #ffffff; font-family: 'Barlow Condensed', 'Arial Narrow', Arial, sans-serif; font-weight: bold; font-size: 18px; letter-spacing: 1px; text-transform: uppercase; padding: 14px 24px; border-radius: 0; text-decoration: none;">${escapeHtml(p.buttonLabel)}</a></p>
</div>
${p.contacts ? contactsEmailHtml(p.contacts.info, p.contacts.labels) : ""}
${footerHtml(p.contacts, p.workshopName)}
</div>
</div>
</body></html>`;
}

/** A plain notice e-mail in the same brand frame (no button), e.g. the declined work reminder; the contacts below. */
export function noticeEmailHtml(p: { workshopName: string; text: string; logoUrl?: string | null; contacts?: MessageContacts | null }): string {
  return `<!doctype html><html><body style="margin: 0; background: #F2F5F7;">
<div style="background: #F2F5F7; padding: 24px 12px; font-family: 'Atkinson Hyperlegible Next', Verdana, Arial, Helvetica, sans-serif; font-size: 16px; line-height: 1.55; color: #0E2433;">
<div style="max-width: 560px; margin: 0 auto; background: #ffffff; border: 2px solid #0E2433; border-radius: 0; overflow: hidden;">
${emailHeaderHtml(p.workshopName, p.logoUrl)}
<div style="padding: 24px;">
<p style="margin: 0;">${textToHtml(p.text)}</p>
</div>
${p.contacts ? contactsEmailHtml(p.contacts.info, p.contacts.labels) : ""}
${footerHtml(p.contacts, p.workshopName)}
</div>
</div>
</body></html>`;
}

/** The text and the link must belong together: the link is the workshop's own quote link. */
export function linkMatchesToken(link: string, token: string): boolean {
  return /^https:\/\/[^\s]+#quote\/[0-9a-f]{64}$/.test(link) && link.endsWith(`#quote/${token}`);
}

/** The same for the customer's intake link (#intake/<token>, feature "intakePhotos"). */
export function intakeLinkMatchesToken(link: string, token: string): boolean {
  return /^https:\/\/[^\s]+#intake\/[0-9a-f]{64}$/.test(link) && link.endsWith(`#intake/${token}`);
}

/** How long the photo and signature links of the customer's intake page work: one hour (the page asks again when opened). */
export const INTAKE_IMAGE_SECONDS = 3600;

/** The same for the customer's "car ready" page (#ready/<token>, 24 hex characters, short for the SMS). */
export function readyLinkMatchesToken(link: string, token: string): boolean {
  return /^https:\/\/[^\s]+#ready\/[0-9a-f]{24}$/.test(link) && link.endsWith(`#ready/${token}`);
}

/**
 * Storage path of the workshop's logo from its settings (settings.logo.path), or null. Same rule as
 * cleanLogo in packages/core/src/logo.ts and public.is_logo_path in the database.
 */
export function logoPathOf(settings: unknown, workshopId: string): string | null {
  const logo = (settings as { logo?: { path?: unknown } } | null)?.logo;
  const path = typeof logo?.path === "string" ? logo.path : "";
  const m = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/logo-[0-9a-z]{1,20}\.(png|jpg)$/.exec(path);
  return m && m[1] === workshopId ? path : null;
}

/** How long the logo link in an e-mail works: 30 days (customers often open a mail days later). */
export const EMAIL_LOGO_SECONDS = 30 * 24 * 3600;
/** How long the logo link of the quote page works: one hour (the page asks again when opened). */
export const QUOTE_LOGO_SECONDS = 3600;

/** The plain text part of a customer e-mail: the text, then the contacts (when there are any). */
export function withContactsText(text: string, contacts?: MessageContacts | null): string {
  const block = [contacts ? contactsText(contacts.info, contacts.labels) : "", operatorLineOf(contacts)].filter(Boolean).join("\n\n");
  return block ? `${text.trimEnd()}\n\n${block}` : text;
}

// currency words and signs of the program's currencies (money.ts), written after or before the number
const MONEY_AFTER = /\d[\d\s.,'  ]*\s?(?:ft|huf|eur|€|euro|czk|kč|pln|zł|ron|lei|rsd|din|дин|uah|грн|₴|usd|\$)(?![\p{L}\d])/iu;
const MONEY_BEFORE = /(?:€|\$|£|₴)\s?\d/u;

/**
 * Whether a message text names an amount of money ("kb. 48 500 Ft", "€ 120", "120 EUR"). The driver's "car ready"
 * notice must never carry one (owner, 2026-11-05: prices go only to the customer); send-quote refuses such a text.
 * Plates, phone numbers, dates and opening hours are not amounts.
 */
export function textHasMoneyAmount(text: string): boolean {
  return MONEY_AFTER.test(text) || MONEY_BEFORE.test(text);
}
